import { useMemo, useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Layout } from "../components/Layout"
import { Button } from "@/components/Button"
import { DataTable } from "@/components/DataTable/DataTable"
import { PageHeader } from "@/components/PageHeader"
import { Skeleton } from "@/components/Skeleton/Skeleton"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { useToast } from "@/components/Toast"
import {
  useGameDetail,
  useDeletedClips,
  useRestoreClip,
  useShrinkGame,
  useRenderLadder,
  useDeleteGame,
  useUpdateGameDate,
} from "@/hooks/useGames"
import { ConfirmDialog, Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/Dialog"
import { DatePicker } from "@/components/DatePicker/DatePicker"
import type {
  AdminClip,
  AdminDeletedClip,
  RenderLadderResponse,
  ShrinkGameResponse,
} from "@/hooks/useGames"
import { deletedClipLength } from "@/lib/deletedClipDisplay"
import config from "@/constants/config"
import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  FilmIcon,
  ServerIcon,
  TagIcon,
  SparklesIcon,
  VideoCameraIcon,
  UserIcon,
  UserGroupIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  TrashIcon,
  PencilIcon,
} from "@heroicons/react/24/outline"
import { ColumnDef } from "@tanstack/react-table"
import { formatDistanceToNow } from "date-fns"

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return "0 B"
  const k = 1024
  const sizes = ["B", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
}

function isEmptyObject(obj: unknown): boolean {
  if (!obj) return true
  if (typeof obj !== "object") return true
  return Object.keys(obj as Record<string, unknown>).length === 0
}

/** Parse a date string as a local calendar date (not UTC). `new Date("2024-01-01")`
 *  treats date-only strings as UTC midnight, which shifts the displayed date west of
 *  UTC. This helper splits date-only strings into year/month/day and constructs a
 *  local-time Date. */
function parseLocalDate(dateStr: string): Date {
  const dateOnlyMatch = /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
  if (dateOnlyMatch) {
    const [y, m, d] = dateStr.split("-").map(Number)
    return new Date(y, m - 1, d)
  }
  return new Date(dateStr)
}

export function GameDetail() {
  const { gameId } = useParams<{ gameId: string }>()
  const navigate = useNavigate()
  const { data, isLoading, error } = useGameDetail(gameId!)
  const [expandedClip, setExpandedClip] = useState<string | null>(null)
  const [dateModalOpen, setDateModalOpen] = useState(false)

  const clipColumns = useMemo<ColumnDef<AdminClip>[]>(
    () => [
      {
        id: "index",
        header: "#",
        cell: ({ row }) => (
          <span className="text-sm text-secondary font-mono">{row.index + 1}</span>
        ),
      },
      {
        accessorKey: "public_id",
        header: "Clip ID",
        cell: ({ row }) => (
          <span className="text-sm font-mono text-secondary">
            {row.original.public_id.slice(0, 12)}...
          </span>
        ),
      },
      {
        accessorKey: "size",
        header: "Size",
        cell: ({ row }) => (
          <span className="text-sm">{formatBytes(row.original.size || 0)}</span>
        ),
      },
      {
        id: "data_status",
        header: "Tagged",
        cell: ({ row }) => {
          const hasData = !isEmptyObject(row.original.data)
          return (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                hasData
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {hasData ? "Tagged" : "Empty"}
            </span>
          )
        },
      },
      {
        id: "predicted_status",
        header: "Predicted",
        cell: ({ row }) => {
          const hasPredicted = !isEmptyObject(row.original.predicted)
          return (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                hasPredicted
                  ? "bg-blue-100 text-blue-800"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {hasPredicted ? "Yes" : "No"}
            </span>
          )
        },
      },
      {
        accessorKey: "created_at",
        header: "Uploaded",
        cell: ({ row }) => (
          <span className="text-sm text-secondary">
            {formatDistanceToNow(new Date(row.original.created_at), { addSuffix: true })}
          </span>
        ),
      },
      {
        id: "expand",
        header: "",
        cell: ({ row }) => {
          const isExpanded = expandedClip === row.original.public_id
          return (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setExpandedClip(isExpanded ? null : row.original.public_id)
              }}
              className="p-1 hover:bg-surface rounded transition-colors"
              title="Show clip data"
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4 text-secondary" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-secondary" />
              )}
            </button>
          )
        },
      },
    ],
    [expandedClip]
  )

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  if (isLoading || !data) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-48" />
              <Skeleton className="h-64" />
            </div>
            <div className="space-y-6">
              <Skeleton className="h-48" />
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  const { game, clips, stats } = data

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          title={
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate("/games")}
                className="p-2 hover:bg-surface rounded-md transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <span>Game Details</span>
            </div>
          }
          actions={
            <a
              href={`${config.frontendUrl}/view-game/${encodeURIComponent(game.public_id)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-contrast hover:bg-primary/90"
            >
              Open game
              <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
            </a>
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Game Info Card */}
            <div className="bg-surface rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Game Information</h2>

              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <VideoCameraIcon className="h-8 w-8 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-medium">
                      {game.title || "Untitled Game"}
                    </h3>
                    <p className="text-sm text-secondary mt-1">{game.type}</p>

                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <p className="text-sm text-secondary">Game ID</p>
                        <p className="font-mono text-sm">{game.public_id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-secondary">Game Date</p>
                        <button
                          onClick={() => setDateModalOpen(true)}
                          className="text-sm flex items-center space-x-1 hover:text-primary transition-colors group"
                        >
                          <span>{parseLocalDate(game.when).toLocaleDateString()}</span>
                          <PencilIcon className="h-3 w-3 text-secondary opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      </div>
                      <div>
                        <div className="flex items-center space-x-1">
                          <UserIcon className="h-4 w-4 text-secondary" />
                          <p className="text-sm text-secondary">Creator</p>
                        </div>
                        <p className="text-sm">{game.creator_name || "Unknown"}</p>
                        <p className="text-xs text-secondary">{game.creator_email}</p>
                      </div>
                      <div>
                        <div className="flex items-center space-x-1">
                          <UserGroupIcon className="h-4 w-4 text-secondary" />
                          <p className="text-sm text-secondary">Team</p>
                        </div>
                        <p className="text-sm">{game.team_name || "No team"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-secondary">Created</p>
                        <p className="text-sm">
                          {new Date(game.created_at).toLocaleDateString()}
                          <span className="text-secondary ml-1">
                            ({formatDistanceToNow(new Date(game.created_at), { addSuffix: true })})
                          </span>
                        </p>
                      </div>
                    </div>

                    {game.uniforms && (
                      <div className="mt-4">
                        <p className="text-sm text-secondary">Uniforms</p>
                        <p className="text-sm mt-1 bg-background rounded p-2">
                          {game.uniforms}
                        </p>
                      </div>
                    )}

                    {game.game_meta && !isEmptyObject(game.game_meta) && (
                      <div className="mt-4">
                        <p className="text-sm text-secondary">Game Metadata</p>
                        <pre className="text-xs mt-1 bg-background rounded p-2 overflow-x-auto">
                          {JSON.stringify(game.game_meta, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Clips Table */}
            <div className="bg-surface rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">
                  Clips ({stats.clips_count})
                </h2>
              </div>

              {clips.length === 0 ? (
                <p className="text-sm text-secondary">No clips uploaded yet.</p>
              ) : (
                <div className="space-y-0">
                  <DataTable columns={clipColumns} data={clips} />

                  {/* Expanded clip detail */}
                  {expandedClip && (
                    <ExpandedClipDetail
                      clip={clips.find((c) => c.public_id === expandedClip)!}
                      onClose={() => setExpandedClip(null)}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Deleted clips (archived, restorable for 30 days) */}
            <DeletedClipsSection gameId={gameId!} />
          </div>

          <div className="space-y-6">
            {/* Stats Card */}
            <div className="bg-surface rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Upload Stats</h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <FilmIcon className="h-5 w-5 text-secondary" />
                    <span className="text-sm">Total Clips</span>
                  </div>
                  <span className="font-medium">{stats.clips_count}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <ServerIcon className="h-5 w-5 text-secondary" />
                    <span className="text-sm">Total Size</span>
                  </div>
                  <span className="font-medium">{formatBytes(stats.total_size)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <TagIcon className="h-5 w-5 text-secondary" />
                    <span className="text-sm">Tagged</span>
                  </div>
                  <span
                    className={`font-medium ${
                      stats.clips_count > 0 && stats.tagged_count === stats.clips_count
                        ? "text-green-600"
                        : ""
                    }`}
                  >
                    {stats.tagged_count} / {stats.clips_count}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <SparklesIcon className="h-5 w-5 text-secondary" />
                    <span className="text-sm">Predicted</span>
                  </div>
                  <span
                    className={`font-medium ${
                      stats.clips_count > 0 && stats.predicted_count === stats.clips_count
                        ? "text-green-600"
                        : ""
                    }`}
                  >
                    {stats.predicted_count} / {stats.clips_count}
                  </span>
                </div>
              </div>
            </div>

            {/* Transcoding controls (retroactive shrink) */}
            <ShrinkGameSection gameId={gameId!} />
            {/* Adaptive delivery: add the missing low-bitrate renditions */}
            <RenderLadderSection gameId={gameId!} />

            {/* Quick Links */}
            {game.team_public_id && (
              <div className="bg-surface rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4">Quick Links</h2>
                <div className="space-y-2">
                  <button
                    onClick={() => navigate(`/teams/${game.team_public_id}`)}
                    className="w-full text-left px-3 py-2 text-sm rounded hover:bg-background transition-colors flex items-center space-x-2"
                  >
                    <UserGroupIcon className="h-4 w-4 text-secondary" />
                    <span>View Team</span>
                  </button>
                  {game.creator_public_id && (
                    <button
                      onClick={() => navigate(`/users/${game.creator_public_id}`)}
                      className="w-full text-left px-3 py-2 text-sm rounded hover:bg-background transition-colors flex items-center space-x-2"
                    >
                      <UserIcon className="h-4 w-4 text-secondary" />
                      <span>View Creator</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Danger zone — permanent delete (ops requests, DMCA takedowns) */}
            <DeleteGameSection gameId={gameId!} title={game.title} />
          </div>
        </div>
      </div>

      <GameDateEditModal
        open={dateModalOpen}
        onOpenChange={setDateModalOpen}
        gameId={gameId!}
        currentDate={game.when}
      />
    </Layout>
  )
}

function ExpandedClipDetail({
  clip,
  onClose,
}: {
  clip: AdminClip
  onClose: () => void
}) {
  if (!clip) return null

  return (
    <div className="border border-border rounded-md p-4 mt-2 bg-background">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium">Clip: {clip.public_id}</h4>
        <button
          onClick={onClose}
          className="text-xs text-secondary hover:text-primary"
        >
          Close
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-medium text-secondary mb-1">Data (User Tagged)</p>
          <pre className="text-xs bg-surface rounded p-2 overflow-x-auto max-h-48">
            {isEmptyObject(clip.data)
              ? "(empty)"
              : JSON.stringify(clip.data, null, 2)}
          </pre>
        </div>
        <div>
          <p className="text-xs font-medium text-secondary mb-1">
            Predicted (Model)
          </p>
          <pre className="text-xs bg-surface rounded p-2 overflow-x-auto max-h-48">
            {isEmptyObject(clip.predicted)
              ? "(empty)"
              : JSON.stringify(clip.predicted, null, 2)}
          </pre>
        </div>
      </div>

      {!isEmptyObject(clip.video_meta) && (
        <div className="mt-3">
          <p className="text-xs font-medium text-secondary mb-1">Video Metadata</p>
          <pre className="text-xs bg-surface rounded p-2 overflow-x-auto max-h-32">
            {JSON.stringify(clip.video_meta, null, 2)}
          </pre>
        </div>
      )}

      <div className="mt-3 flex items-center space-x-4 text-xs text-secondary">
        <span>Size: {formatBytes(clip.size || 0)}</span>
        <span>Video URL: {clip.video_url}</span>
      </div>
    </div>
  )
}

function DeletedClipsSection({ gameId }: { gameId: string }) {
  const { toast } = useToast()
  const { data, isLoading, error } = useDeletedClips(gameId)
  const restore = useRestoreClip(gameId)

  const columns = useMemo<ColumnDef<AdminDeletedClip>[]>(
    () => [
      {
        id: "restore_position",
        header: "Position",
        cell: ({ row }) => (
          <span className="text-sm font-mono text-secondary" title="Where Restore would place this clip">
            → #{row.original.restore_position + 1}
          </span>
        ),
      },
      {
        id: "type",
        header: "Type",
        cell: ({ row }) => {
          const virtual = row.original.is_virtual
          return (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                virtual ? "bg-purple-100 text-purple-800" : "bg-amber-100 text-amber-800"
              }`}
            >
              {virtual ? "Virtual" : "Real"}
            </span>
          )
        },
      },
      {
        id: "length",
        header: "Length",
        cell: ({ row }) => <span className="text-sm">{deletedClipLength(row.original)}</span>,
      },
      {
        accessorKey: "deleted_at",
        header: "Deleted",
        cell: ({ row }) => (
          <span
            className="text-sm text-secondary"
            title={new Date(row.original.deleted_at).toLocaleString()}
          >
            {formatDistanceToNow(new Date(row.original.deleted_at), { addSuffix: true })}
          </span>
        ),
      },
      {
        accessorKey: "public_id",
        header: "Clip ID",
        cell: ({ row }) => (
          <span className="text-sm font-mono text-secondary">
            {row.original.public_id.slice(0, 12)}...
          </span>
        ),
      },
      {
        id: "restore",
        header: "",
        cell: ({ row }) => {
          const clip = row.original
          const pending = restore.isPending && restore.variables?.publicId === clip.public_id
          return (
            <Button
              variant="outline"
              size="sm"
              disabled={restore.isPending}
              onClick={() =>
                restore.mutate(
                  { publicId: clip.public_id },
                  {
                    onSuccess: () =>
                      toast({
                        title: "Clip restored",
                        description: `${clip.public_id.slice(0, 12)}… is back in the active set.`,
                      }),
                    onError: (err) =>
                      toast({
                        title: "Couldn't restore clip",
                        description: err.message,
                        variant: "destructive",
                      }),
                  }
                )
              }
            >
              {pending ? "Restoring…" : "Restore"}
            </Button>
          )
        },
      },
    ],
    [restore, toast]
  )

  // Quietly hide the whole section unless there's something to show. (Errors are
  // non-critical here — the active clips view above still renders.)
  if (isLoading || error || !data || data.clips.length === 0) return null

  return (
    <div className="bg-surface rounded-lg p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold">Deleted clips ({data.clips.length})</h2>
      </div>
      <p className="text-sm text-secondary mb-4">
        Archived clips, restorable for 30 days before they are purged. Position shows where Restore
        would slot the clip back into the active list.
      </p>
      {data.truncated && (
        <p className="text-sm text-amber-700 mb-4">
          Showing the {data.clips.length} most recently deleted clips; older archived clips are not
          listed.
        </p>
      )}
      <DataTable columns={columns} data={data.clips} />
    </div>
  )
}

// Retroactive shrink controls (#978). Re-encodes each distinct whole-file source under
// the bitrate ceiling and overwrites its transcoded copy. Safety: we re-encode FROM the
// retained original and never delete it, and the worker's savings guard prevents a
// non-shrinking re-encode from bloating the file. Quota recount + before/after size are
// deferred fast-follows, so the toast is careful not to promise a quota drop yet.
function ShrinkGameSection({ gameId }: { gameId: string }) {
  const { toast } = useToast()
  const shrink = useShrinkGame(gameId)
  const [force, setForce] = useState(false)
  const [forceShrink, setForceShrink] = useState(false)
  const [result, setResult] = useState<ShrinkGameResponse | null>(null)

  const run = () => {
    const message = forceShrink
      ? "Force shrink EVERY source, ignoring guardrails? This re-encodes and overwrites the transcoded copy even when it would NOT normally shrink (bypasses the worker's <10%-savings revert). Originals are kept."
      : force
        ? "Force re-shrink EVERY source in this game? This re-encodes and overwrites the transcoded copy even for sources already at/below the ceiling. Originals are kept."
        : "Shrink this game's sources that are above the bitrate ceiling? Re-encodes from the retained original and overwrites the transcoded copy."
    if (!window.confirm(message)) return
    shrink.mutate(
      { force, force_shrink: forceShrink },
      {
        onSuccess: (data) => {
          setResult(data)
          toast({
            title:
              data.enqueued > 0
                ? `Shrink started for ${data.enqueued} source${data.enqueued === 1 ? "" : "s"}`
                : "Nothing to shrink",
            description:
              data.enqueued > 0
                ? `Ceiling ${data.bitrate_ceiling_mbps} Mbps${
                    forceShrink ? " · guardrails off" : force ? " · forced" : ""
                  }. Re-encoding now; originals are kept.`
                : "No eligible sources — see the per-source detail below.",
          })
        },
        onError: (err) =>
          toast({
            title: "Couldn't shrink game",
            description: err.message,
            variant: "destructive",
          }),
      }
    )
  }

  return (
    <div className="bg-surface rounded-lg p-6">
      <h2 className="text-lg font-semibold mb-1">Transcoding</h2>
      <p className="text-sm text-secondary mb-4">
        Re-shrink this game's source videos under the bitrate ceiling. Re-encodes from the
        retained original and overwrites the transcoded copy — originals are never deleted.
      </p>
      <label className="flex items-center space-x-2 mb-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={force}
          onChange={(e) => setForce(e.target.checked)}
          className="rounded border-border"
        />
        <span>Force (re-encode even if already shrunk)</span>
      </label>
      <label className="flex items-center space-x-2 mb-4 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={forceShrink}
          onChange={(e) => setForceShrink(e.target.checked)}
          className="rounded border-border"
        />
        <span title="Re-encode even when the file would not normally shrink — bypasses the worker's <10%-savings revert guard.">
          Force (ignore guardrails)
        </span>
      </label>
      <Button variant="outline" size="sm" disabled={shrink.isPending} onClick={run}>
        {shrink.isPending
          ? "Starting…"
          : forceShrink
            ? "Force shrink (no guardrails)"
            : force
              ? "Force re-shrink"
              : "Shrink game"}
      </Button>

      {result && (
        <div className="mt-4 space-y-2">
          <p className="text-sm font-medium">
            {result.enqueued} enqueued · {result.sources.length - result.enqueued} skipped
          </p>
          {result.sources.length > 0 && (
            <ul className="text-xs text-secondary space-y-1">
              {result.sources.map((s) => (
                <li
                  key={s.clip_public_id}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="font-mono truncate" title={s.video_url}>
                    {s.clip_public_id.slice(0, 14)}…
                  </span>
                  <span
                    className={
                      s.action === "enqueued" ? "text-green-700 font-medium" : "text-secondary"
                    }
                  >
                    {s.action === "enqueued" ? "enqueued" : `skipped: ${s.reason}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

// "Add missing renditions" (#1578). Renders the low-bitrate rung + progressive backup for
// each transcoded source of the game that lacks them for its current bytes. Publish-once:
// nothing existing is replaced, so this is safe to click twice; a source mid-render is
// deduped and reported as skipped. Mirrors the shrink section's confirm → mutate → toast +
// per-source readout.
function RenderLadderSection({ gameId }: { gameId: string }) {
  const { toast } = useToast()
  const render = useRenderLadder(gameId)
  const [result, setResult] = useState<RenderLadderResponse | null>(null)
  // Set when the last page was truncated; the next click continues from it.
  const [cursor, setCursor] = useState<string | null>(null)

  const run = () => {
    if (
      !cursor &&
      !window.confirm(
        "Add the missing low-bitrate renditions for this game's sources? Existing renditions are never replaced; sources that already have them are skipped."
      )
    )
      return
    render.mutate(cursor ? { after: cursor } : {}, {
      onSuccess: (data) => {
        setResult(data)
        setCursor(data.next_cursor)
        toast({
          title:
            data.dispatched > 0
              ? `Rendering started for ${data.dispatched} source${data.dispatched === 1 ? "" : "s"}`
              : "Nothing to render",
          description:
            data.dispatched > 0
              ? "The rung publishes on its own when the job finishes; the master playlist picks it up within a minute."
              : "Every source already has its renditions, is mid-render, or is not worth one — see the per-source detail below.",
        })
      },
      onError: (err) =>
        toast({
          title: "Couldn't add renditions",
          description: err.message,
          variant: "destructive",
        }),
    })
  }

  return (
    <div className="bg-surface rounded-lg p-6">
      <h2 className="text-lg font-semibold mb-1">Renditions</h2>
      <p className="text-sm text-secondary mb-4">
        Add the missing low-bitrate rendition (HLS rung + progressive backup) for each source of
        this game. Existing renditions are never replaced.
      </p>
      <Button variant="outline" size="sm" disabled={render.isPending} onClick={run}>
        {render.isPending
          ? "Starting…"
          : cursor
            ? "Continue with the next sources"
            : "Add missing renditions"}
      </Button>

      {result && (
        <div className="mt-4 space-y-2">
          <p className="text-sm font-medium">
            {result.dispatched} dispatched · {result.sources.length - result.dispatched} skipped
            {result.truncated ? " · more sources remain — continue" : ""}
          </p>
          {result.sources.length > 0 && (
            <ul className="text-xs text-secondary space-y-1">
              {result.sources.map((s) => (
                <li key={s.source_key} className="flex items-center justify-between gap-2">
                  <span className="font-mono truncate" title={s.source_key}>
                    {s.source_key.split("/").pop()}
                  </span>
                  <span
                    className={
                      s.action === "dispatched" ? "text-green-700 font-medium" : "text-secondary"
                    }
                  >
                    {s.action === "dispatched"
                      ? `dispatched${s.labels && s.labels.length > 0 ? ` · ${s.labels.join(", ")}` : ""}${s.progressive ? " · backup" : ""}`
                      : `skipped: ${s.reason}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

// Danger zone: permanently delete a game and all its clips. For occasional ops
// requests (e.g. a user who can't self-serve a delete) and DMCA takedowns. Reuses
// the superuser-authorized DELETE /v1/games/delete; the backend frees R2 objects
// after responding and 409s if the game is mid-exchange. A confirmation dialog is
// required for destructive admin actions.
function DeleteGameSection({ gameId, title }: { gameId: string; title: string | null }) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const deleteGame = useDeleteGame()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const run = () => {
    deleteGame.mutate(
      { publicId: gameId },
      {
        onSuccess: () => {
          toast({ title: "Game deleted" })
          navigate("/games")
        },
        onError: (err) =>
          toast({
            title: "Couldn't delete game",
            description: err.message,
            variant: "destructive",
          }),
      }
    )
  }

  return (
    <div className="bg-surface rounded-lg p-6 border border-red-200">
      <h2 className="text-lg font-semibold mb-1 text-red-700">Danger zone</h2>
      <p className="text-sm text-secondary mb-4">
        Permanently delete this game and all of its clips. This cannot be undone, and
        the video files are removed from storage. Use for ops requests and DMCA
        takedowns.
      </p>
      <Button
        variant="destructive"
        size="sm"
        disabled={deleteGame.isPending}
        onClick={() => setConfirmOpen(true)}
      >
        <TrashIcon className="h-4 w-4 mr-1.5" />
        {deleteGame.isPending ? "Deleting…" : "Delete game"}
      </Button>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete this game?"
        description={`Permanently delete "${
          title || "Untitled game"
        }" and all of its clips. This cannot be undone.`}
        confirmLabel="Delete game"
        confirmVariant="destructive"
        onConfirm={run}
      />
    </div>
  )
}

function GameDateEditModal({
  open,
  onOpenChange,
  gameId,
  currentDate,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  gameId: string
  currentDate: string
}) {
  const { toast } = useToast()
  const updateDate = useUpdateGameDate(gameId)
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    () => parseLocalDate(currentDate)
  )

  // Reset to the server's current date whenever the modal opens, so a
  // previously cancelled edit doesn't persist across open/close cycles.
  useEffect(() => {
    if (open) {
      setSelectedDate(parseLocalDate(currentDate))
    }
  }, [open, currentDate])

  const handleSave = () => {
    if (!selectedDate) return
    // Send as yyyy-MM-dd to preserve the calendar date across time zones.
    // toISOString() would shift the date for timezones west of UTC.
    const yyyy = selectedDate.getFullYear()
    const mm = String(selectedDate.getMonth() + 1).padStart(2, "0")
    const dd = String(selectedDate.getDate()).padStart(2, "0")
    updateDate.mutate(
      { when: `${yyyy}-${mm}-${dd}` },
      {
        onSuccess: () => {
          toast({ title: "Game date updated" })
          onOpenChange(false)
        },
        onError: (err) =>
          toast({
            title: "Couldn't update game date",
            description: err.message,
            variant: "destructive",
          }),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Change Game Date</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <DatePicker
            date={selectedDate}
            onSelect={setSelectedDate}
            defaultMonth={selectedDate}
          />
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!selectedDate || updateDate.isPending}
              onClick={handleSave}
            >
              {updateDate.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
